// keyset 페이지네이션 + 무결성 계약의 공용 엔진 — 순수 로직 (I/O는 주입).
// 배송정보 stale 스캔의 order_item·parsed_order 조회가 공유한다 (구현 codexloop 합의, 2026-07-29).
//
// 계약(하나라도 어기면 verified=false — 호출부는 스캔 실패로 처리, 잘린 데이터 제안 금지):
//  · 커서 없는 base predicate의 exact count를 페이징 전(baseline)·후(final) 두 번 — 동등해야 함
//  · count null = 실패
//  · keyset(id > lastId, id ASC) 페이징 — offset의 삽입/삭제 밀림 없음
//  · 행 id는 number·유일·단조 증가, isRowExpected(행이 요청 집합에 속함) 충족
//  · distinct 로드 수 === baseline
//  · 종료는 "빈 페이지"에서만 — PostgREST max_rows가 요청 limit보다 작아도 짧은 페이지로
//    조기 종료하지 않는다. 단 비어있지 않은 페이지가 커서를 전진시키지 못하면(전 행 malformed)
//    같은 페이지를 영원히 다시 받으므로 즉시 실패로 끊는다(무한루프 가드).
//  · maxPages 초과 = 실패로 끊음(지속 삽입 기아·비정상 응답 방어)
//
// count가 보존되는 삭제+삽입 조합은 이 계약으로도 놓칠 수 있다 — 그 창은 apply RPC의
// order_item 튜플 비교가 최종 방어선이다(플랜 합의).

export interface KeysetScanIo<T> {
  /** 커서 없는 base predicate의 exact count (head-only). 실패 시 throw. */
  fetchCount(): Promise<number | null>;
  /** id > afterId, id ASC, limit 페이지. 실패 시 throw. */
  fetchPage(afterId: number, limit: number): Promise<T[]>;
}

export interface KeysetScanOptions<T> {
  pageSize: number;
  /** 이 페이지 수를 넘기면 실패 처리. 기본 400 (pageSize 500 기준 20만 행 — 실사용 최대의 수십 배). */
  maxPages?: number;
  getId(row: T): unknown;
  isRowExpected(row: T): boolean;
}

export interface KeysetScanResult<T> {
  rows: T[];
  verified: boolean;
}

export async function runVerifiedKeysetScan<T>(
  io: KeysetScanIo<T>,
  opts: KeysetScanOptions<T>
): Promise<KeysetScanResult<T>> {
  const maxPages = opts.maxPages ?? 400;
  const baseline = await io.fetchCount();

  const rows: T[] = [];
  const seenIds = new Set<number>();
  let lastId = 0;
  let pageOk = true;
  let pages = 0;

  for (;;) {
    if (pages >= maxPages) {
      pageOk = false;
      break;
    }
    pages += 1;

    const pageStartId = lastId;
    const page = await io.fetchPage(lastId, opts.pageSize);
    if (page.length === 0) break;

    for (const row of page) {
      const id = opts.getId(row);
      if (
        typeof id !== 'number' ||
        !Number.isFinite(id) ||
        id <= lastId ||
        seenIds.has(id) ||
        !opts.isRowExpected(row)
      ) {
        pageOk = false;
      }
      if (typeof id === 'number' && Number.isFinite(id)) {
        seenIds.add(id);
        if (id > lastId) lastId = id;
      }
      rows.push(row);
    }

    // 무한루프 가드: 비어있지 않은 페이지가 커서를 전진시키지 못했다 = 같은 페이지 반복 확정.
    if (lastId <= pageStartId) {
      pageOk = false;
      break;
    }
  }

  const final = await io.fetchCount();

  const verified =
    pageOk &&
    baseline !== null &&
    final !== null &&
    baseline === final &&
    seenIds.size === baseline;

  return { rows, verified };
}
